import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useAppState } from "../redux"
import { PricingRule } from '../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/rules'
import { useUpdatePricingVersion } from './pricing-version'
import { QueryKeys } from '../../constants/query-keys'

export const usePricingVersionRules = (id: string | number | undefined) => {
  const appState = useAppState()
  const { csrf_timestamp, csrf_token, masterAccountId } = appState.session

  const getPricingVersionRules = async (): Promise<PricingRule | PricingRule[] | undefined> => {
    const response = await axios.get(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${id}/rules?csrf_token=${csrf_token}&csrf_timestamp=${csrf_timestamp}`)
    if (response.data?.data?.length === 1) {
      return response.data.data[0] as PricingRule
    }
    if (response.data?.data?.length > 1) {
      return response.data.data as PricingRule[]
    }
    return undefined
  }

  const query = useQuery([QueryKeys.GLO_JAM_PRICING_RULES, { id }], getPricingVersionRules, { enabled: !!id })

  return query
}

export const useVersionRulesCache = (id: string) => {
  const key = [QueryKeys.GLO_JAM_PRICING_RULES, { id }]
  const queryClient = useQueryClient()
  const cache = queryClient.getQueryData<PricingRule>(key)
  return cache as PricingRule
}

export const useUpdateVersionRules = (versionId: string, replaceAll?: boolean) => {
  const appState = useAppState()
  const queryClient = useQueryClient()
  const { masterAccountId } = appState.session
  const rules = useVersionRulesCache(versionId)
  const { mutate: updateVersion } = useUpdatePricingVersion(versionId)

  const updateVersionRules = async (fieldsToUpdate: Partial<PricingRule>) => {
    const newRules = replaceAll ? fieldsToUpdate : { ...rules, ...fieldsToUpdate }
    const validatedFilters = !newRules?.jobsFilter?.length ? [] : newRules.jobsFilter?.filter(jf => jf.value && jf.value.trim().length)
    const updatedRules = { ...newRules, jobsFilter: validatedFilters }
    const response = await axios.put(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules/${updatedRules?.id}`, updatedRules)
    return response.data
  }

  const mutation = useMutation({
    mutationFn: updateVersionRules,
    onMutate: async (fieldsToUpdate) => {
      await queryClient.cancelQueries()
      const updatedRules = { ...rules, ...fieldsToUpdate }
      queryClient.setQueryData([QueryKeys.GLO_JAM_PRICING_RULES, { id: versionId }], updatedRules)
      return rules
    },
    onSettled: () => {
      updateVersion({})
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_RULES, { id: versionId }])
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_REVIEW_MATCHES])
    },
    onError: (error, _) => {
      console.error('Error updating rules:', error);
    }
  })

  return mutation
}

export const useSelectRuleAndRemoveOthers = ({ onSuccess }: { onSuccess: () => void }) => {
  const appState = useAppState()
  const queryClient = useQueryClient()
  const { masterAccountId } = appState.session

  const selectRuleAndRemoveOthers = async ({ ruleIds }: { ruleIds: number[] }) => {
    if (ruleIds?.length) return Promise.all(ruleIds.map(async (ruleId) => {
      const response = await axios.delete(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules/${ruleId}`)
      return response
    }))
    return []
  }

  const mutation = useMutation({
    mutationFn: selectRuleAndRemoveOthers,
    onSuccess,
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_RULES])
    },
    onError: (error, _) => {
      console.error('Error selecting and removing rules:', error);
    }
  })

  return mutation
}
